/* ScalperIntel — product description tabs (Phase 6).

   Finds every `.si-tabs` wrapper inside `.product__description`, scans
   its DIRECT-child <h3> elements, and rewrites the wrapper into a tabbed
   UI:

     <div class="si-tabs" data-si-tabs-ready>
       <nav class="si-tabs__nav">
         <button class="si-tabs__tab is-active" data-tab="...">Overview</button>
         ...
       </nav>
       <div class="si-tabs__panel is-active" id="...">...</div>
       ...
     </div>

   The original <h3> labels become tab buttons. Everything between two
   <h3>s becomes the panel for the previous tab. URL hash deep-links work
   (`?...#features` jumps to that tab). No-op when no `.si-tabs` exists. */

(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function slugify(s) {
    return String(s)
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  function initTabs(root) {
    if (root.hasAttribute('data-si-tabs-ready')) return;

    // Direct-child <h3>s become tab labels.
    var headings = Array.prototype.filter.call(root.children, function (el) {
      return el.tagName === 'H3';
    });
    if (headings.length === 0) return;

    // Build new structure: nav (desktop horizontal tabs) + panels with
    // their own headings (mobile accordion). CSS swaps which UI is visible
    // at the 750px breakpoint — desktop hides the panel headings, mobile
    // hides the nav and shows the headings as accordion triggers.
    var nav = document.createElement('nav');
    nav.className = 'si-tabs__nav';

    var list = document.createElement('div');
    list.className = 'si-tabs__list';
    list.setAttribute('role', 'tablist');
    nav.appendChild(list);

    var panels = [];
    var usedIds = {};

    headings.forEach(function (heading, i) {
      var label = (heading.textContent || '').trim();
      var base = 'si-tab-' + (slugify(label) || 'tab') + '-' + i;
      var id = base;
      // Defensive: avoid id collisions across multiple .si-tabs on the
      // same page or across products in design-mode previews.
      while (usedIds[id]) id = base + '-' + Math.random().toString(36).slice(2, 6);
      usedIds[id] = true;

      // Desktop tab button.
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'si-tabs__tab' + (i === 0 ? ' is-active' : '');
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.setAttribute('aria-controls', id);
      btn.dataset.tab = id;
      btn.textContent = label;
      list.appendChild(btn);

      // Panel — wraps a mobile-only heading + a body div. The body holds
      // the actual content. CSS hides the body on mobile when the panel
      // isn't .is-active (accordion collapsed state).
      var panel = document.createElement('section');
      panel.className = 'si-tabs__panel' + (i === 0 ? ' is-active' : '');
      panel.id = id;
      panel.setAttribute('role', 'tabpanel');

      var head = document.createElement('button');
      head.type = 'button';
      head.className = 'si-tabs__head';
      head.dataset.tab = id;
      head.setAttribute('aria-controls', id + '-body');
      head.setAttribute('aria-expanded', i === 0 ? 'true' : 'false');

      var headLabel = document.createElement('span');
      headLabel.className = 'si-tabs__head-label';
      headLabel.textContent = label;

      var headIcon = document.createElement('span');
      headIcon.className = 'si-tabs__head-icon';
      headIcon.setAttribute('aria-hidden', 'true');

      head.appendChild(headLabel);
      head.appendChild(headIcon);
      panel.appendChild(head);

      var body = document.createElement('div');
      body.className = 'si-tabs__body';
      body.id = id + '-body';

      // Move every sibling between this <h3> and the NEXT <h3> into the body.
      var next = heading.nextElementSibling;
      while (next && next.tagName !== 'H3') {
        var toMove = next;
        next = next.nextElementSibling;
        body.appendChild(toMove);
      }

      panel.appendChild(body);
      panels.push(panel);
    });

    // Replace root content. Headings are removed (their text lives in the
    // tab buttons + panel headings now).
    headings.forEach(function (h) { h.remove(); });
    while (root.firstChild) root.removeChild(root.firstChild);

    root.appendChild(nav);
    panels.forEach(function (p) { root.appendChild(p); });
    root.setAttribute('data-si-tabs-ready', '');

    // Desktop tab click → switch tab.
    list.addEventListener('click', function (e) {
      var btn = e.target.closest('.si-tabs__tab');
      if (!btn) return;
      activate(btn.dataset.tab);
      if (history.replaceState) {
        history.replaceState(null, '', '#' + btn.dataset.tab);
      }
    });

    // Mobile accordion heading click → expand that panel (or collapse it
    // if it's already the open one).
    root.addEventListener('click', function (e) {
      var head = e.target.closest('.si-tabs__head');
      if (!head) return;
      var tabId = head.dataset.tab;
      var panel = document.getElementById(tabId);
      if (!panel) return;
      if (panel.classList.contains('is-active')) {
        // Already open → collapse it (mobile-only behavior; on desktop
        // the heading is hidden so this won't fire there).
        collapseAll();
      } else {
        activate(tabId);
        if (history.replaceState) {
          history.replaceState(null, '', '#' + tabId);
        }
      }
    });

    function activate(tabId) {
      list.querySelectorAll('.si-tabs__tab').forEach(function (b) {
        var on = b.dataset.tab === tabId;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('.si-tabs__panel').forEach(function (p) {
        var on = p.id === tabId;
        p.classList.toggle('is-active', on);
        var head = p.querySelector('.si-tabs__head');
        if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    function collapseAll() {
      // Mobile: clicking the open heading closes it (no panel active).
      // Desktop: this is unreachable because the heading is hidden via CSS.
      list.querySelectorAll('.si-tabs__tab').forEach(function (b) {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      root.querySelectorAll('.si-tabs__panel').forEach(function (p) {
        p.classList.remove('is-active');
        var head = p.querySelector('.si-tabs__head');
        if (head) head.setAttribute('aria-expanded', 'false');
      });
    }

    // If URL hash matches one of our tab IDs, jump there on load.
    if (window.location.hash) {
      var hashId = window.location.hash.slice(1);
      if (root.querySelector('#' + CSS.escape(hashId))) {
        activate(hashId);
      }
    }
  }

  ready(function () {
    document.querySelectorAll('.product__description .si-tabs').forEach(initTabs);
  });

  // Re-init on Shopify theme editor section reload (design-mode previews).
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target) return;
    event.target.querySelectorAll('.product__description .si-tabs').forEach(initTabs);
  });
})();
